import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';

// Zocdoc libraries
import LayoutHeader from '@zocdoc-frontend-common/components-header';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { EVENT_INITIATORS, INTERACTION_TYPES } from '@zocdoc/frontend-metrics';
import { blue100, white } from '@zocdoc-mezzanine/foundations-colors';
import { Link } from '@zocdoc-frontend-common/components-link';
import { Button } from '@zocdoc-frontend-common/components-button';
import { Arrow } from '@zocdoc-frontend-common/assets-icons/Arrow';

// BWA
import SecureLogo from '../SecureLogo';
import { paths } from '../../paths';
import { PatientNavLinks } from './constants';

export default class Header extends Component {
    static propTypes = {
        location: PropTypes.object.isRequired,
        showSecureHeader: PropTypes.bool.isRequired,
        partnershipImageUrl: PropTypes.string,
        onClickLoginLink: PropTypes.func,
        goBackToPatientInfo: PropTypes.func,
        showBlizzardPage: PropTypes.bool,
        showSignUpPage: PropTypes.bool,
        sendMetric: PropTypes.func,
    };

    render() {
        return (
            <StyledLayoutHeader
                hideUserArea
                showBlizzardPage={this.props.showBlizzardPage}
                showSignUpPage={this.props.showSignUpPage}
            >
                {this.maybeRenderBackButton()}
                <HeaderSubComponentGroup
                    showSignUpPage={this.props.showSignUpPage}
                >
                    {this.mayBeRenderLoginButton()}
                    {this.maybeRenderNavigationLinks()}
                    {this.maybeRenderSecureLock()}
                    {this.maybeRenderPartnership()}
                </HeaderSubComponentGroup>
            </StyledLayoutHeader>
        );
    }

    handleClickLoginLink = () => {
        const { onClickLoginLink, showSignUpPage, sendMetric } = this.props;

        sendMetric({
            action: 'Clicked header login link',
            category: showSignUpPage
                ? 'Booking - Sign Up'
                : 'Booking - Patient Info',
            component: 'Header',
            element: 'Login link',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
            section: showSignUpPage ? 'Sign Up View' : 'Patient Info View',
        });

        onClickLoginLink();
    };

    onClickBackToPatientInfo = () => {
        const { goBackToPatientInfo, sendMetric } = this.props;
        sendMetric({
            action: 'Clicked back button',
            category: 'Booking - Sign Up',
            component: 'Back button',
            element: 'Back button',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
            section: 'Sign Up View',
        });
        goBackToPatientInfo();
    };

    maybeRenderBackButton() {
        return (
            this.props.showSignUpPage && (
                <BackButtonMobile
                    data-test="header-back-to-patient-info-page-button"
                    variation={Button.VARIATION.LINK}
                    onClick={this.onClickBackToPatientInfo}
                >
                    <Arrow direction={Arrow.direction.left} />
                </BackButtonMobile>
            )
        );
    }

    mayBeRenderLoginButton() {
        const shouldRenderDivider =
            this.shouldRenderNavigationLinks() ||
            this.props.partnershipImageUrl ||
            this.props.showSecureHeader;

        return (
            this.props.showBlizzardPage && (
                <LoginButtonWrapper>
                    <LoginButton
                        data-test="header-open-login-modal-button"
                        variation={Button.VARIATION.LINK}
                        onClick={this.handleClickLoginLink}
                    >
                        Log in
                    </LoginButton>
                    {shouldRenderDivider && <Divider>|</Divider>}
                </LoginButtonWrapper>
            )
        );
    }

    maybeRenderNavigationLinks() {
        if (this.shouldRenderNavigationLinks()) {
            return this.renderNavigationLinks(PatientNavLinks);
        }
    }

    shouldRenderNavigationLinks() {
        const { partnershipImageUrl, showSecureHeader } = this.props;
        return (
            this.isBookingCompletePage() &&
            !partnershipImageUrl && // no partnership image
            !showSecureHeader
        );
    }

    isBookingCompletePage() {
        const { location } = this.props;
        return (
            location &&
            location.pathname &&
            location.pathname.startsWith(paths.bookingCompletePath)
        );
    }

    renderNavigationLinks(userNavLinks) {
        return (
            <NavLinkGroup data-test="header-nav-group">
                {userNavLinks.map(navLink => {
                    return this.renderNavigationLink(navLink);
                })}
            </NavLinkGroup>
        );
    }

    renderNavigationLink(navLink) {
        return (
            <NavLinkDiv key={`header-nav-links-${navLink.dataTest}`}>
                <StyledLink
                    data-test={`header-nav-links-${navLink.dataTest}`}
                    href={navLink.url}
                >
                    {navLink.message}
                </StyledLink>
            </NavLinkDiv>
        );
    }

    maybeRenderPartnership() {
        const { partnershipImageUrl } = this.props;
        if (partnershipImageUrl) {
            return (
                <PartnershipWrapper data-test="partnership-image">
                    <PartnershipText>In partnership with</PartnershipText>
                    <PartnershipImg
                        role="presentation"
                        src={partnershipImageUrl}
                    />
                </PartnershipWrapper>
            );
        }
    }

    maybeRenderSecureLock() {
        const { showSecureHeader, partnershipImageUrl } = this.props;
        if (showSecureHeader || partnershipImageUrl) {
            return <SecureLogo />;
        }
    }
}

const HeaderSubComponentGroup = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-end;
    margin: -30px 0px -15px 120px;
    z-index: 5;

    ${p =>
        p.showSignUpPage &&
        css`
            margin-top: -18px;
        `}

    ${breakpoint.small} {
        ${p =>
            p.showSignUpPage &&
            css`
                margin-top: -30px;
            `}
    }
`;

const StyledLayoutHeader = styled(LayoutHeader)`
    ${p =>
        p.isInlineBooking
            ? css`
                  a {
                      pointer-events: none;
                  }
              `
            : ''}

    ${p =>
        p.showBlizzardPage &&
        css`
            background: ${white};
        `}

    ${p =>
        p.showSignUpPage &&
        css`
            &:first-child > :first-child {
                display: none;
            }
        `}

    min-height: 56px;

    ${breakpoint.small} {
        min-height: 64px;
        ${p =>
            p.showSignUpPage &&
            css`
                &:first-child > :first-child {
                    display: block;
                }
            `}
    }
`;

const StyledLink = styled(Link)`
    border-bottom-color: ${blue100};
    color: ${blue100};
    display: inline-block;
    font: 15px ${FontFamily.SharpSansSemibold};
    position: relative;
    z-index: 2;
`;

const NavLinkGroup = styled.div`
    display: none;
    flex-wrap: nowrap;
    margin: 10px 10px 10px;

    ${breakpoint.medium} {
        display: flex;
    }
`;

const NavLinkDiv = styled.div`
    display: none;
    margin-right: 25px;

    ${breakpoint.medium} {
        display: block;
    }
`;

const PartnershipWrapper = styled.div`
    display: none;
    margin: -10px 32px 0px 0px;

    ${breakpoint.small} {
        display: block;
    }
`;

const PartnershipText = styled.div`
    display: none;
    font: 10px/11px ${FontFamily.SharpSansSemibold};
    padding: 0px;

    ${breakpoint.small} {
        display: block;
    }
`;

const PartnershipImg = styled.img`
    display: none;
    height: 44px;

    ${breakpoint.small} {
        display: block;
    }
`;

const LoginButton = styled(Button)`
    color: ${blue100};
    text-decoration-line: underline;
    font: 13px/1 ${FontFamily.SharpSansSemibold};

    ${breakpoint.small} {
        font-size: 15px;
    }
`;

const Divider = styled.span`
    margin: 0px 15px 0px 5px;
    font: 13px/1 ${FontFamily.SharpSansSemibold};
    display: none;

    ${breakpoint.xsmall} {
        display: inline;
    }

    ${breakpoint.small} {
        font-size: 15px;
        line-height: 20px;
    }
`;

const LoginButtonWrapper = styled.div`
    z-index: 6;
    padding-top: 3px;

    ${breakpoint.small} {
        padding-top: 0px;
        padding-bottom: 3px;
    }
`;

const BackButtonMobile = styled(Button)`
    color: ${blue100};
    font-size: 15px;
    position: absolute;
    top: 6px;
    left: 3px;
    ${breakpoint.small} {
        display: none;
    }
`;
